import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { QRCodeSVG } from 'qrcode.react';
import { getLocalSessaoContext, quickSignupAndCheckin } from '../services/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/Card';
import { Loader2, CheckCircle, XCircle, User, Mail, Building2, GraduationCap, Download, QrCode, Calendar, Clock, MapPin, AlertTriangle } from 'lucide-react'; // Importando AlertTriangle
import { Skeleton } from '../components/ui/Skeleton';

function QuickCheckinSignupPage() {
    const [searchParams] = useSearchParams();
    const qrCodeKey = searchParams.get('qrcode') || searchParams.get('localId'); // AGORA PROCURA POR 'qrcode' OU 'localId'
    const navigate = useNavigate();
    const qrCodeRef = useRef(null); // Ref para o componente QRCodeSVG

    const [context, setContext] = useState(null);
    const [loadingContext, setLoadingContext] = useState(true);
    const [error, setError] = useState('');
    
    const [formData, setFormData] = useState({
        nomeParticipante: '',
        emailParticipante: '',
        grauAcademicoParticipante: '',
        instituicaoParticipante: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successData, setSuccessData] = useState(null); // Armazena a resposta de sucesso

    // 1. Fetch Context
    useEffect(() => {
        const fetchContext = async () => {
            if (!qrCodeKey) {
                setError('Chave de QR Code do local da sessão ausente na URL.');
                setLoadingContext(false);
                return;
            }
            
            setLoadingContext(true);
            try {
                // Usa o qrCodeKey (UUID) para buscar o contexto
                const data = await getLocalSessaoContext(qrCodeKey);
                setContext(data);
            } catch (err) {
                console.error('Erro ao buscar contexto:', err);
                setError(err.message || 'Não foi possível carregar os detalhes do local da sessão.');
                toast.error(err.message || 'Erro ao carregar contexto.');
            } finally {
                setLoadingContext(false);
            }
        };
        fetchContext();
    }, [qrCodeKey]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (!formData.nomeParticipante || !formData.emailParticipante) {
            setError('Nome e Email são obrigatórios.');
            return;
        }

        // CRITICAL CHECK: Ensure context is loaded and IDs are valid
        if (!context || !context.eventoId || !context.localSessaoId) {
            setError('Erro de contexto: Detalhes do evento ou local da sessão estão ausentes.');
            return;
        }

        setIsSubmitting(true);

        const payload = {
            ...formData,
            // O backend espera o ID numérico do local da sessão para o quickSignupAndCheckin
            localSessaoId: context.localSessaoId, 
            eventoId: context.eventoId, 
        };

        try {
            const response = await quickSignupAndCheckin(payload);
            
            // Verifica se o backend retornou o código QR
            if (!response.codigoQrParticipante) {
                throw new Error("Check-in realizado, mas o código QR do participante não foi retornado.");
            }

            setSuccessData(response);
            toast.success(response.message || 'Cadastro e Check-in realizados com sucesso!');
        } catch (err) {
            console.error('Erro no Cadastro Rápido:', err);
            setError(err.message || 'Ocorreu um erro ao tentar cadastrar e fazer check-in.');
            toast.error(err.message || 'Erro no check-in.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDownloadQrCode = () => {
        if (!qrCodeRef.current) {
            toast.error("QR Code não está pronto para download.");
            return;
        }

        try {
            // Acessa o elemento SVG/Canvas gerado pelo qrcode.react
            const svgElement = qrCodeRef.current.querySelector('svg');
            if (!svgElement) {
                toast.error("Elemento QR Code não encontrado.");
                return;
            }

            // Converte SVG para Data URL
            const svgData = new XMLSerializer().serializeToString(svgElement);
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();
            
            img.onload = () => {
                // Define o tamanho do canvas para uma alta resolução (ex: 1024x1024)
                const targetSize = 1024; 
                canvas.width = targetSize;
                canvas.height = targetSize;
                
                // Desenha a imagem SVG no canvas na resolução alvo
                ctx.drawImage(img, 0, 0, targetSize, targetSize);
                
                // Converte Canvas para PNG Blob/Data URL
                const pngFile = canvas.toDataURL('image/png');
                
                const link = document.createElement('a');
                link.href = pngFile;
                link.download = `qrcode_participante_${successData.nomeParticipante.replace(/\s/g, '_')}.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast.success("QR Code baixado com sucesso!");
            };
            
            // Define a fonte da imagem como o SVG Data URL
            img.src = 'data:image/svg+xml;base64,' + btoa(svgData);

        } catch (error) {
            console.error('Erro ao baixar QR Code:', error);
            toast.error('Erro ao baixar QR Code. Tente novamente.');
        }
    };

    const LoadingSkeleton = () => (
        <Card className="w-full max-w-md mx-auto">
            <CardHeader className="text-center">
                <Skeleton className="h-8 w-3/4 mx-auto mb-2" />
                <Skeleton className="h-4 w-full" />
            </CardHeader>
            <CardContent className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
            </CardContent>
            <CardFooter><Skeleton className="h-10 w-full" /></CardFooter>
        </Card>
    );

    if (loadingContext) {
        return <div className="flex items-center justify-center min-h-screen bg-background p-4"><LoadingSkeleton /></div>;
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-background p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <XCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
                        <CardTitle className="text-2xl">Erro de Acesso</CardTitle>
                        <CardDescription className="leading-relaxed text-destructive">{error}</CardDescription>
                    </CardHeader>
                    <CardFooter className="flex justify-center">
                        <Button onClick={() => navigate('/eventos')}>Voltar aos Eventos</Button>
                    </CardFooter>
                </Card>
            </div>
        );
    }

    // Tela de Sucesso
    if (successData) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-background p-4">
                <Card className="w-full max-w-md text-center">
                    <CardHeader>
                        <CheckCircle className="h-10 w-10 text-success mx-auto mb-3" />
                        <CardTitle className="text-2xl text-success">Sucesso!</CardTitle>
                        <CardDescription className="leading-relaxed text-foreground font-medium">{successData.message}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">Seu cadastro e check-in foram registrados para a sessão:</p>
                        <p className="text-lg font-bold text-primary">{context.nomeSessao}</p>
                        
                        <div className="p-4 border rounded-lg bg-muted/50">
                            <h3 className="text-md font-semibold mb-3 flex items-center justify-center"><QrCode className="mr-2 h-5 w-5" /> Seu Código QR Pessoal</h3>
                            <div ref={qrCodeRef} className="flex justify-center p-2 bg-white rounded-md mx-auto w-fit">
                                {/* Aumentando o tamanho de exibição para 256px */}
                                <QRCodeSVG value={successData.codigoQrParticipante} size={256} level="H" /> 
                            </div>
                            
                            {/* NOVO DESTAQUE E AVISO */}
                            <p className="text-sm font-bold text-primary mt-4">
                                Guarde este código para futuros check-ins.
                            </p>
                            <div className="mt-3 p-3 bg-destructive/10 border border-destructive/50 rounded-md flex items-start text-left">
                                <AlertTriangle className="h-5 w-5 text-destructive mr-2 flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-destructive">
                                    Atenção: Este QR Code é a sua chave de acesso. Se você fechar esta página sem baixá-lo, não poderá vê-lo novamente sem fazer login.
                                </p>
                            </div>
                            
                            <Button onClick={handleDownloadQrCode} className="mt-4 w-full" variant="outline">
                                <Download className="mr-2 h-4 w-4" /> Baixar QR Code (PNG)
                            </Button>
                        </div>
                        
                        <p className="text-sm text-muted-foreground pt-2">
                            Você pode <Link to="/login" className="underline hover:text-primary">fazer login</Link> para gerenciar sua conta (sua senha foi enviada por email).
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Tela de Formulário
    return (
        <div className="flex items-center justify-center min-h-screen bg-background p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <QrCode className="h-10 w-10 text-primary mx-auto mb-3" />
                    <CardTitle className="text-2xl">Check-in Rápido</CardTitle>
                    <CardDescription className="leading-relaxed">
                        Você está se registrando e fazendo check-in para:
                    </CardDescription>
                    <div className="mt-2 p-3 border rounded-md bg-secondary/50 text-left space-y-1">
                        <p className="text-sm font-semibold text-foreground flex items-center"><Calendar className="mr-2 h-4 w-4" /> Evento: {context.nomeEvento}</p>
                        <p className="text-sm font-medium text-muted-foreground flex items-center"><Clock className="mr-2 h-4 w-4" /> Sessão: {context.nomeSessao}</p>
                        <p className="text-sm font-medium text-muted-foreground flex items-center"><MapPin className="mr-2 h-4 w-4" /> Local: {context.nomeLocal}</p>
                    </div>
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent className="space-y-4">
                        {error && <p className="text-sm font-medium text-destructive">{error}</p>}

                        <div className="grid gap-2">
                            <Label htmlFor="nomeParticipante" className="mb-1 flex items-center"><User className="mr-2 h-4 w-4" /> Nome Completo</Label>
                            <Input 
                                id="nomeParticipante" 
                                name="nomeParticipante" 
                                value={formData.nomeParticipante} 
                                onChange={handleChange} 
                                required 
                                disabled={isSubmitting} 
                                placeholder="Seu Nome Completo"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="emailParticipante" className="mb-1 flex items-center"><Mail className="mr-2 h-4 w-4" /> Email</Label>
                            <Input 
                                id="emailParticipante" 
                                name="emailParticipante" 
                                type="email" 
                                value={formData.emailParticipante} 
                                onChange={handleChange} 
                                required 
                                disabled={isSubmitting} 
                                placeholder="m@exemplo.com"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="grauAcademicoParticipante" className="mb-1 flex items-center"><GraduationCap className="mr-2 h-4 w-4" /> Grau Acadêmico (Opcional)</Label>
                            <Input 
                                id="grauAcademicoParticipante" 
                                name="grauAcademicoParticipante" 
                                value={formData.grauAcademicoParticipante} 
                                onChange={handleChange} 
                                disabled={isSubmitting} 
                                placeholder="Ex: Licenciatura, Mestrado"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="instituicaoParticipante" className="mb-1 flex items-center"><Building2 className="mr-2 h-4 w-4" /> Instituição (Opcional)</Label>
                            <Input 
                                id="instituicaoParticipante" 
                                name="instituicaoParticipante" 
                                value={formData.instituicaoParticipante} 
                                onChange={handleChange} 
                                disabled={isSubmitting} 
                                placeholder="Nome da sua instituição"
                            />
                        </div>
                    </CardContent>
                    <CardFooter>
                        <Button className="w-full" type="submit" disabled={isSubmitting}>
                            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
                            Cadastrar e Fazer Check-in
                        </Button>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
}

export default QuickCheckinSignupPage;